import {
	signIn as awsSignIn,
	signOut as awsSignOut,
	signUp as awsSignUp,
	resendSignUpCode as awsResendSignUpCode,
	confirmSignUp as awsConfirmSignUp,
	SignUpOutput,
	AuthError,
	resetPassword as awsResetPassword,
	confirmResetPassword as awsConfirmResetPassword,
} from "aws-amplify/auth";
import { useCallback, useEffect } from "react";
import { awsErrorInterpreter } from "../util/awsErrors";
import { useObservableReducer } from "./useObservableReducer";
import { initializeSession, SessionAction, SessionState, sessionStore } from "../libs/sessionLib";
import { Hub } from "@aws-amplify/core";
import logger from "../libs/errorLib";
import { navigate } from "vike/client/router";

export type SessionStateAPI = {
	session: SessionState;
	signIn: (email: string, password: string) => Promise<void>;
	signOut: () => Promise<void>;
	signUp: (email: string, password: string) => Promise<SignUpOutput>;
	resendConfirmationCode: (email: string) => Promise<void>;
	resetPassword: (email: string) => Promise<void>;
	confirmResetPassword: (email: string, confirmationCode: string, newPassword: string) => Promise<void>;
	confirmSignUp: (email: string, confirmationCode: string) => Promise<void>;
};

export function useSessionState(): SessionStateAPI {
	const [state, dispatch] = useObservableReducer<SessionState, SessionAction>(sessionStore);
	useEffect(() => {
		if (!state.isAuthenticated && state.isAuthenticating) {
			logger.debug("Initialising session");
			void initializeSession(dispatch);
		}
	}, [dispatch, state.isAuthenticated, state.isAuthenticating]);

	useEffect(() => {
		const listener = (data: { payload: { event: string } }) => {
			const { event } = data.payload;
			switch (event) {
				case "signedIn":
				case "tokenRefresh":
				case "signedOut":
					logger.debug("Auth event: " + event);
					void initializeSession(dispatch);
					break;
				default:
      				logger.debug("Other auth event:" + event);
			}
		};
		Hub.listen("auth", listener);
	}, [dispatch]);

	async function signIn(email: string, password: string): Promise<void> {
		try {
			const signInResult = await awsSignIn({ username: email, password });
			if (!signInResult.isSignedIn && signInResult.nextStep.signInStep === "CONFIRM_SIGN_UP")
				throw new AuthError({
					name: "UserNotConfirmedException",
					message: "User is not confirmed",
				});
			dispatch({ type: "setAuthenticated", value: true });
		} catch (e: unknown) {
			dispatch({ type: "setAuthenticated", value: false });
			throw awsErrorInterpreter(e);
		}
	}

	const signOut = useCallback(async () => {
		try {
			await awsSignOut();
		} catch (e) {
			throw awsErrorInterpreter(e);
		}
		dispatch({ type: "setAuthenticated", value: false });
		void navigate("/login");
	}, [dispatch]);

	async function signUp(email: string, password: string) {
		try {
			return await awsSignUp({
				username: email,
				password,
			});
		} catch (e) {
			throw awsErrorInterpreter(e);
		}
	}

	async function resendConfirmationCode(email: string) {
		try {
			await awsResendSignUpCode({ username: email });
		} catch (e) {
			throw awsErrorInterpreter(e);
		}
	}

	async function confirmSignUp(email: string, confirmationCode: string) {
		try {
			await awsConfirmSignUp({ username: email, confirmationCode });
		} catch (e) {
			throw awsErrorInterpreter(e);
		}
	}

	async function resetPassword(email: string) {
		try {
			await awsResetPassword({
				username: email,
			});
		} catch (e) {
			throw awsErrorInterpreter(e);
		}
	}

	async function confirmResetPassword(email: string, confirmationCode: string, newPassword: string) {
		try {
			await awsConfirmResetPassword({
				username: email,
				newPassword,
				confirmationCode,
			});
		} catch (e) {
			throw awsErrorInterpreter(e);
		}
	}

	return {
		session: state,
		signIn,
		signOut,
		signUp,
		resendConfirmationCode,
		resetPassword,
		confirmResetPassword,
		confirmSignUp,
	};
}
